/**
 * Training takes real time (การฝึกใช้เวลาจริง): one neigong or move at a time trains for a while —
 * on in the background, and on while the game is closed. Its cultivation cost (ค่าฝึก) is drained
 * a little every few seconds as it trains; when there is not enough, training waits.
 */
import { MAX_SKILL_LEVEL, neigongCost, skillCost } from './cultivation';
import { NEIGONG } from './data/neigong';
import type { PlayerProgress } from './progress';

/** Seconds between charges of the training cost. */
export const TRAIN_TICK = 5;

export type TrainKind = 'skill' | 'neigong';

export interface TrainingState {
  kind: TrainKind;
  id: string;
  /** The level reached when done. */
  level: number;
  /** Seconds left and in all. */
  left: number;
  total: number;
  /** The whole cost, and how much of it has been paid so far. */
  cost: number;
  paid: number;
  /** Seconds trained since the last charge. */
  tick: number;
}

export interface TrainingStep {
  /** Levels that finished on this step (already applied), oldest first. */
  done: TrainingState[];
  /** Each charge taken, in cultivation. */
  charges: number[];
  /** Not enough cultivation for the next charge: training is waiting. */
  starved: boolean;
}

/** Seconds to raise a neigong from `level`: one minute at first, two hours toward level 20. */
export const neigongTrainSeconds = (level: number): number =>
  Math.round((60 * 120 ** ((Math.max(1, level) - 1) / 18)) / 10) * 10;

/** Seconds to raise a move from `level`: twenty seconds at first, fifteen minutes at the top. */
export const skillTrainSeconds = (level: number): number =>
  Math.round((20 * 45 ** ((Math.max(1, level) - 1) / (MAX_SKILL_LEVEL - 2))) / 10) * 10;

export const trainSeconds = (kind: TrainKind, level: number): number =>
  kind === 'neigong' ? neigongTrainSeconds(level) : skillTrainSeconds(level);

/** Cultivation owed once `elapsed` of the training's seconds are done. */
const owed = (t: TrainingState, elapsed: number) => Math.round((t.cost * elapsed) / t.total) - t.paid;

/** The level a neigong or move can be trained to. */
export const trainMaxLevel = (kind: TrainKind, id: string): number =>
  kind === 'neigong' ? (NEIGONG[id]?.maxLevel ?? 0) : MAX_SKILL_LEVEL;

/** Fresh training from `level` to the next: its cost and how long it takes. */
export function newTraining(kind: TrainKind, id: string, level: number): TrainingState {
  const total = trainSeconds(kind, level);
  const cost = kind === 'neigong' ? neigongCost(level) : skillCost(level);
  return { kind, id, level: level + 1, left: total, total, cost, paid: 0, tick: 0 };
}

/**
 * Moves training on by `seconds` (a simulation step, or the time the game was closed), charging
 * its cost every TRAIN_TICK seconds trained. Time only counts while the next charge can be paid.
 * When a level finishes it is applied; with auto-training on, the next level starts at once.
 */
export function advanceTraining(pr: PlayerProgress, seconds: number): TrainingStep {
  const out: TrainingStep = { done: [], charges: [], starved: false };
  let left = seconds > 0 ? seconds : 0;
  for (let t = pr.training; t; t = pr.training) {
    while (left > 0 && t.left > 0) {
      const chunk = Math.min(TRAIN_TICK - t.tick, t.left);
      const due = owed(t, t.total - t.left + chunk);
      if (pr.cultivation < due) {
        out.starved = true;
        break;
      }
      const step = Math.min(left, chunk);
      left -= step;
      t.tick += step;
      t.left = Math.max(0, t.left - step);
      if (t.tick < TRAIN_TICK - 1e-9 && t.left > 0) continue;
      t.tick = 0;
      if (due > 0) {
        pr.cultivation -= due;
        pr.cultivationSpent += due;
        t.paid += due;
        out.charges.push(due);
      }
    }
    if (t.left > 0) break;
    const levels = t.kind === 'neigong' ? pr.neigong : pr.skillLevels;
    levels[t.id] = Math.max(levels[t.id] ?? 1, t.level);
    out.done.push(t);
    pr.training =
      pr.trainAuto && t.level < trainMaxLevel(t.kind, t.id) ? newTraining(t.kind, t.id, t.level) : null;
  }
  return out;
}
